// Reading a failed API call, apart from the shared client, so UI can read errors without loading it (it reads
// localStorage on import).

/**
 * The error body the API answers with: `{ error: { message, type } }`. A 403 `chat_blocked` adds `until` and
 * `removed` (a timeout's end and removed false; a removal's until null and removed true).
 */
export interface ApiErrorBody<Type extends string = string> {
  error: { message: string; type: Type; until?: string | null; removed?: boolean }
}

/** A failed API call, flattened. `status` is null when no response arrived. */
export interface ApiFailure<Type extends string = string> {
  status: number | null
  type: Type | null
  message: string | null
  until: string | null
  /** Present when the body says: a 403 `chat_blocked` for a removal (no end) is true, for a timeout false. */
  removed?: boolean
  /** How long the response's Retry-After header asks to wait (a 429 names the throttle's window), or null. */
  retryAfterMs: number | null
}

/**
 * Reads a rejected API call: its HTTP status, the `{ error: { message, type, until?, removed? } }` body, and the
 * Retry-After header (exposed by the API's CORS config, so the web app can read it).
 */
export function apiFailure<Type extends string = string>(error: unknown): ApiFailure<Type> {
  const response = (error as { response?: { status?: unknown; data?: unknown; headers?: unknown } } | null)?.response
  const body = (response?.data as Partial<ApiErrorBody> | undefined)?.error
  const text = (value: unknown) => (typeof value === 'string' ? value : null)
  return {
    status: typeof response?.status === 'number' ? response.status : null,
    type: text(body?.type) as Type | null,
    message: text(body?.message),
    until: text(body?.until),
    ...(typeof body?.removed === 'boolean' ? { removed: body.removed } : {}),
    retryAfterMs: parseRetryAfter(header(response?.headers, 'retry-after')),
  }
}

/** A response header by name, whatever its case (axios's AxiosHeaders, or a plain object). */
function header(headers: unknown, name: string): unknown {
  if (!headers || typeof headers !== 'object') return null
  const wanted = name.toLowerCase()
  const entry = Object.entries(headers).find(([key]) => key.toLowerCase() === wanted)
  return entry ? entry[1] : null
}

/**
 * A Retry-After header's wait in milliseconds: whole seconds (`"60"`) or an HTTP date, which counts from
 * `now` (a past date is no wait). Null when there is none or it can't be read.
 */
export function parseRetryAfter(value: unknown, now = Date.now()): number | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000
  // An HTTP date names its day and month; without a letter, Date.parse would read "1.5" or "-3" as dates.
  if (!/[a-z]/i.test(trimmed)) return null
  const at = Date.parse(trimmed)
  return Number.isNaN(at) ? null : Math.max(0, at - now)
}
